import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Form, Button, Container, Row, Col, Card, Alert, Spinner } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import WeekSelector from "../weekSelector";
import DriverSelector from "../../components/driverSelector";
import { useTranslation } from 'react-i18next';
import { useLocation } from "react-router-dom";
import { AiOutlinePlus, AiOutlineSave } from "react-icons/ai";

const parseMoneyItems = (value) => {
    if (Array.isArray(value)) return value;
    if (typeof value === "string") {
        try {
            const parsed = JSON.parse(value || "[]");
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    }
    return [];
};

const filterMoneyItems = (items) =>
    (items || []).filter((item) => {
        const amount = (item?.amount ?? "").toString().trim();
        const desc = (item?.description ?? "").toString().trim();
        return amount !== "" || desc !== "";
    });

const reconcileClientItems = (items, total) => {
    const filtered = filterMoneyItems(parseMoneyItems(items));
    const sum = filtered.reduce((s, item) => s + (parseFloat(String(item.amount).replace(",", ".")) || 0), 0);
    const scalar = parseFloat(String(total ?? "").replace(",", ".")) || 0;
    if (filtered.length === 0 && scalar !== 0) {
        return [{ amount: String(scalar), description: "" }];
    }
    const diff = Math.round((scalar - sum) * 100) / 100;
    if (diff > 0.005) {
        return [...filtered, { amount: diff.toFixed(2), description: "" }];
    }
    return filtered;
};

const ExtraFundsEdit = () => {
    const { t } = useTranslation();
    const token = localStorage.getItem("token");

    const [formData, setFormData] = useState({
        driver_id: "",
        week_number: null,
        returnVat: "0",
        taxesZus: "0",
        partner: "0",
        accountant: "0",
        carRent: "0",
        other: "0",
        sallary: "0",
        otherExpenses: [],
        refundsItems: [],
        otherDesc: "",
        refunds: "0",
        refundsDesc: "",
        createdAt: new Date().toISOString().slice(0, 10)
    });
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [weekHasRecords, setWeekHasRecords] = useState(false);
    const [expenseDescriptions, setExpenseDescriptions] = useState([]);
    const [refundDescriptions, setRefundDescriptions] = useState([]);
    const [loadingDefaults, setLoadingDefaults] = useState(false);
    const navigate = useNavigate();
    const { id } = useParams();
    const location = useLocation();

    // Функції для роботи з localStorage
    const loadDescriptionsFromStorage = useCallback(() => {
        const savedExpenseDescs = localStorage.getItem('expenseDescriptions');
        const savedRefundDescs = localStorage.getItem('refundDescriptions');
        
        if (savedExpenseDescs) {
            try {
                setExpenseDescriptions(JSON.parse(savedExpenseDescs));
            } catch (e) {
                console.error('Error parsing expense descriptions from localStorage', e);
            }
        }
        
        if (savedRefundDescs) {
            try {
                setRefundDescriptions(JSON.parse(savedRefundDescs));
            } catch (e) {
                console.error('Error parsing refund descriptions from localStorage', e);
            }
        }
    }, []);

    const saveExpenseDescription = useCallback((description) => {
        if (!description || description.trim() === '') return;
        
        const trimmedDesc = description.trim();
        const updatedDescs = [...new Set([trimmedDesc, ...expenseDescriptions])].slice(0, 20); // Зберігаємо максимум 20 описів
        setExpenseDescriptions(updatedDescs);
        localStorage.setItem('expenseDescriptions', JSON.stringify(updatedDescs));
    }, [expenseDescriptions]);

    const saveRefundDescription = useCallback((description) => {
        if (!description || description.trim() === '') return;
        
        const trimmedDesc = description.trim();
        const updatedDescs = [...new Set([trimmedDesc, ...refundDescriptions])].slice(0, 20); // Зберігаємо максимум 20 описів
        setRefundDescriptions(updatedDescs);
        localStorage.setItem('refundDescriptions', JSON.stringify(updatedDescs));
    }, [refundDescriptions]);

    const fetchUsers = useCallback(async () => {
        try {
            const response = await axios.get(`/api/users`, {
              headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
            });
            const formattedUsers = response.data.map(user => ({
                value: user.id,
                label: `${user.firstName} ${user.lastName}`
            }));
            setUsers(formattedUsers);
        } catch (error) {
            console.error("Error fetching users", error);
        }
    }, []);

    const fetchFundById = useCallback(async (fundId) => {
        try {
            const response = await axios.get(`/api/extraFunds/${fundId}`, {
              headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
            });
            const fundData = response.data;

            setFormData((prevFormData) => ({
                ...prevFormData,
                ...fundData,
                returnVat: fundData.returnVat ?? fundData.returnVAT ?? prevFormData.returnVat,
                taxesZus: fundData.taxesZus ?? fundData.taxesZUS ?? prevFormData.taxesZus,
                otherExpenses: reconcileClientItems(
                    fundData.other_expenses ?? fundData.otherExpenses,
                    fundData.other
                ),
                refundsItems: reconcileClientItems(
                    fundData.refundsDesc ?? fundData.refundsItems,
                    fundData.refunds
                ),
            }));

            if (fundData.driver_id) {
                setSelectedUser(users.find(user => user.value === fundData.driver_id) || null);
            }
        } catch (error) {
            console.error("❌ Error fetching extra fund", error);
        }
    }, [users]);

    // Підставити стандартні значення водія при створенні запису (коли вибрано водія)
    useEffect(() => {
        if (id || !selectedUser?.value) return;
        const fetchDriverDefaults = async () => {
            const minDelayMs = 1000;
            const startedAt = Date.now();
            setLoadingDefaults(true);
            try {
                const res = await axios.get(
                    `/api/extra-funds-default-values/by-driver/${selectedUser.value}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                if (res.data) {
                    const d = res.data;
                    setFormData((prev) => ({
                        ...prev,
                        returnVat: d.returnVat != null && d.returnVat !== "" ? d.returnVat : prev.returnVat,
                        taxesZus: d.taxesZus != null && d.taxesZus !== "" ? d.taxesZus : prev.taxesZus,
                        partner: d.partner != null && d.partner !== "" ? d.partner : prev.partner,
                        accountant: d.accountant != null && d.accountant !== "" ? d.accountant : prev.accountant,
                        carRent: d.carRent != null && d.carRent !== "" ? d.carRent : prev.carRent,
                        sallary: d.sallary != null && d.sallary !== "" ? d.sallary : prev.sallary,
                        otherExpenses: Array.isArray(d.otherExpenses) && d.otherExpenses.length > 0 ? d.otherExpenses : prev.otherExpenses,
                        refundsItems: Array.isArray(d.refundsItems) && d.refundsItems.length > 0 ? d.refundsItems : prev.refundsItems,
                    }));
                }
            } catch (err) {
                if (err.response?.status !== 404) {
                    console.error("❌ Error loading driver default values", err);
                }
            } finally {
                const elapsed = Date.now() - startedAt;
                if (elapsed < minDelayMs) {
                    await new Promise((r) => setTimeout(r, minDelayMs - elapsed));
                }
                setLoadingDefaults(false);
            }
        };
        fetchDriverDefaults();
    }, [id, selectedUser?.value, token]);

    useEffect(() => {
        fetchUsers();
        loadDescriptionsFromStorage();
    }, [fetchUsers, loadDescriptionsFromStorage]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const driverIdParam = params.get("driver_id");
        const weekNumberParam = params.get("week_number");

        if (driverIdParam && users.length > 0) {
            const matchedUser = users.find(u => u.value.toString() === driverIdParam.toString());
            if (matchedUser) {
                setSelectedUser(matchedUser);
                setFormData(prev => ({ ...prev, driver_id: matchedUser.value }));
            }
        }

        if (weekNumberParam) {
            setFormData(prev => ({ ...prev, week_number: parseInt(weekNumberParam) }));
        }
    }, [users, location.search]);


    useEffect(() => {
        if (id && users.length > 0) fetchFundById(id);
    }, [id, users, fetchFundById]);

    useEffect(() => {
        const checkWeekRecords = async () => {
            if (formData.week_number) {
                try {
                    const res = await axios.get("/api/extraFunds", {
                        params: { week_number: formData.week_number },
                        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
                    });
                    const hasAny = (res.data || []).some(f => String(f.week_number) === String(formData.week_number));
                    setWeekHasRecords(hasAny);
                } catch (e) {
                    console.error("Error checking week records", e);
                }
            }
        };
        checkWeekRecords();
    }, [formData.week_number]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleAddExpense = () => {
        setFormData({
            ...formData,
            otherExpenses: [...(formData.otherExpenses || []), { amount: "", description: "" }]
        });
    };

    const handleExpenseChange = (index, field, value) => {
        const updatedExpenses = [...formData.otherExpenses];
        updatedExpenses[index][field] = value;
        setFormData({ ...formData, otherExpenses: updatedExpenses });
    };

    const handleRemoveExpense = (index) => {
        const updatedExpenses = formData.otherExpenses.filter((_, i) => i !== index);
        setFormData({ ...formData, otherExpenses: updatedExpenses });
    };

    const handleAddRefundItem = () => {
        setFormData({
            ...formData,
            refundsItems: [...(formData.refundsItems || []), { amount: "", description: "" }]
        });
    };

    const handleRefundItemChange = (index, field, value) => {
        const updatedItems = [...(formData.refundsItems || [])];
        if (!updatedItems[index]) updatedItems[index] = { amount: "", description: "" };
        updatedItems[index][field] = value;
        setFormData({ ...formData, refundsItems: updatedItems });
    };

    const handleRemoveRefundItem = (index) => {
        const updatedItems = (formData.refundsItems || []).filter((_, i) => i !== index);
        setFormData({ ...formData, refundsItems: updatedItems });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!selectedUser || !selectedUser.value) {
            alert(t("chooseDriverAlert"));
            return;
        }

        const filteredOtherExpenses = (formData.otherExpenses || []).filter(e => {
            const amount = (e?.amount ?? "").toString().trim();
            const desc = (e?.description ?? "").toString().trim();
            return amount !== "" || desc !== "";
        }).map(e => ({ amount: (e.amount ?? "").toString().replace(',', '.'), description: (e.description ?? "").toString().trim() }));

        const filteredRefundsItems = (formData.refundsItems || []).filter(r => {
            const amount = (r?.amount ?? "").toString().trim();
            const desc = (r?.description ?? "").toString().trim();
            return amount !== "" || desc !== "";
        }).map(r => ({ amount: (r.amount ?? "").toString().replace(',', '.'), description: (r.description ?? "").toString().trim() }));

        const totalOther = filteredOtherExpenses.reduce((sum, exp) => sum + (parseFloat(exp.amount) || 0), 0);
        const totalRefunds = filteredRefundsItems.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);

        const dataToSend = {
            driver_id: selectedUser ? selectedUser.value : "",
            week_number: formData.week_number || 0,
            returnVat: parseFloat(formData.returnVat) || 0,
            taxesZus: parseFloat(formData.taxesZus) || 0,
            partner: parseFloat(formData.partner) || 0,
            accountant: parseFloat(formData.accountant) || 0,
            carRent: parseFloat(formData.carRent) || 0,
            other: totalOther,
            sallary: parseFloat(formData.sallary) || 0,
            otherExpenses: filteredOtherExpenses,
            refunds: totalRefunds,
            refundsDesc: JSON.stringify(filteredRefundsItems)
        };

        const allZero =
            parseFloat(formData.returnVat || 0) === 0 &&
            parseFloat(formData.taxesZus || 0) === 0 &&
            parseFloat(formData.partner || 0) === 0 &&
            parseFloat(formData.accountant || 0) === 0 &&
            parseFloat(formData.carRent || 0) === 0 &&
            parseFloat(formData.sallary || 0) === 0 &&
            parseFloat(totalRefunds || 0) === 0 &&
            totalOther === 0;

        if (allZero && id) {
            const confirmDelete = window.confirm(t("confirmDeleteZeroRecord") || "Усі значення дорівнюють 0. Бажаєте видалити запис?");
            if (confirmDelete) {
                try {
                    await axios.delete(`/api/extraFunds/${id}`, {
                      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
                    });
                    navigate("/admin/driver-extra-funds");
                    return;
                } catch (deleteError) {
                    console.error("Error deleting zero record", deleteError);
                    alert(t("errorDeletingZeroRecord") || "Помилка при видаленні запису.");
                    return;
                }
            }
        }


        try {
            if (id) {
                await axios.put(`/api/extraFunds/${id}`, dataToSend, {
                  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
                });
            } else {
                await axios.post("/api/extraFunds", dataToSend, {
                   headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
                });
            }
            
            // Зберігаємо описи у localStorage після успішного збереження
            filteredOtherExpenses.forEach(expense => {
                if (expense.description && expense.description.trim()) {
                    saveExpenseDescription(expense.description);
                }
            });
            
            filteredRefundsItems.forEach(item => {
                if (item.description && item.description.trim()) {
                    saveRefundDescription(item.description);
                }
            });
            
            navigate("/admin/driver-extra-funds");
        } catch (error) {
            console.error("Error saving extra fund", error);
            const serverError = error.response?.data?.error;
            if (error.response?.status === 409) {
                alert(serverError || t("recordAlreadyExists") || "Запис для цього водія за цей тиждень вже існує");
                return;
            }
            alert(serverError || t("errorSaving") || "Помилка збереження запису.");
        }
    };

    return (
        <Container>
            <h2>{id ? t("edit") : t("add")} {t("extraCosts").toLocaleLowerCase()}</h2>
            {!id && weekHasRecords && (
                <Alert variant="warning">{t("weekHasSomeRecords")}</Alert>
            )}

            {!id && loadingDefaults && (
                <Alert variant="light" className="d-flex align-items-center gap-2 mb-3">
                    <Spinner animation="border" size="sm" />
                    <span>{t("loadingDefaultValues") || "Завантаження стандартних значень…"}</span>
                </Alert>
            )}

            <Form onSubmit={handleSubmit}>
                <Row className="g-2 align-items-stretch" style={{ marginBottom: "20px" }}>
                    <Col xs={12} md={6}>
                        <Form.Group className="mb-0 w-100 d-flex flex-column">
                            <Form.Label className="small mb-1 flex-shrink-0" style={{ minHeight: "20px", lineHeight: "20px" }}>{t("driver")}</Form.Label>
                            <div className="w-100 flex-grow-1" style={{ height: "38px" }}>
                                <DriverSelector
                                    selectedDriver={selectedUser}
                                    setSelectedDriver={setSelectedUser}
                                    className="extra-funds-driver-select"
                                    activeOnly={!id}
                                />
                            </div>
                        </Form.Group>
                    </Col>
                    <Col xs={12} md={6}>
                        <Form.Group className="mb-0 w-100 d-flex flex-column">
                            <Form.Label className="small mb-1 flex-shrink-0" style={{ minHeight: "20px", lineHeight: "20px" }}>{t("week")}</Form.Label>
                            <div className="w-100 flex-grow-1" style={{ height: "38px" }}>
                                <WeekSelector
                                    selectedWeek={formData.week_number}
                                    setSelectedWeek={(week) => setFormData({ ...formData, week_number: week })}
                                    className="mb-0 w-100"
                                />
                            </div>
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col md={6}>
                        <Card className="p-3 border bg-light" >
                            <Form.Group>
                                <Form.Label>{t("refunds")} {t("vat")}</Form.Label>
                                <Form.Control type="number" name="returnVat" value={formData.returnVat} onChange={handleChange} />
                            </Form.Group>
                            <Form.Group>
                                <Form.Label>{t("taxes")} {t("zus")}</Form.Label>
                                <Form.Control type="number" name="taxesZus" value={formData.taxesZus} onChange={handleChange} />
                            </Form.Group>
                        </Card>
                    </Col>
                    <Col md={6}>
                        <Card className="p-3 border bg-light" >
                            <Form.Group>
                                <Form.Label>{t("payment")} {t("partner")}</Form.Label>
                                <Form.Control type="number" name="partner" value={formData.partner} onChange={handleChange} />
                            </Form.Group>
                            <Form.Group>
                                <Form.Label>{t("accountant")}</Form.Label>
                                <Form.Control type="number" name="accountant" value={formData.accountant} onChange={handleChange} />
                            </Form.Group></Card>
                    </Col>
                </Row>
                <Row style={{ marginBottom: "20px" }}>
                    <Col md={12}>
                        <Form.Group>
                            <Form.Label>{t("carRent")}</Form.Label>
                            <Form.Control type="number" name="carRent" value={formData.carRent} onChange={handleChange} />
                        </Form.Group>
                    </Col>
                </Row>
                <Row style={{ marginBottom: "20px" }}>
                    <Col md={6}>
                        <Card className="p-3 border bg-light " >
                            <Form.Group>
                                <div className="d-flex align-items-center justify-content-between mb-2">
                                    <Form.Label className="mb-0">{t("otherCosts")}</Form.Label>
                                    <Button variant="outline-success" size="sm" onClick={handleAddExpense} className="d-flex align-items-center gap-1">
                                        <AiOutlinePlus size={18} />
                                        {t("add")}
                                    </Button>
                                </div>
                                {(formData.otherExpenses || []).map((expense, index) => (
                                    <div key={index} className="d-flex mb-2">
                                        <Form.Control
                                            type="number"
                                            placeholder={t("value")}
                                            value={expense.amount}
                                            onChange={(e) => handleExpenseChange(index, "amount", e.target.value)}
                                            className="me-2"
                                        />
                                        <Form.Control
                                            type="text"
                                            placeholder={t("description")}
                                            value={expense.description}
                                            onChange={(e) => handleExpenseChange(index, "description", e.target.value)}
                                            className="me-2"
                                            list={`expense-descriptions-${index}`}
                                        />
                                        <datalist id={`expense-descriptions-${index}`}>
                                            {expenseDescriptions.map((desc, descIndex) => (
                                                <option key={descIndex} value={desc} />
                                            ))}
                                        </datalist>
                                        <Button variant="danger" size="sm" onClick={() => handleRemoveExpense(index)}>🗑️</Button>
                                    </div>
                                ))}
                            </Form.Group>

                            <Form.Group>
                                <Form.Label>{t("sallary")} ({t("accountant").toLocaleLowerCase()})</Form.Label>
                                <Form.Control type="number" name="sallary" value={formData.sallary} onChange={handleChange} />
                            </Form.Group>
                        </Card>

                    </Col>
                    <Col md={6}>
                        <Card className="p-3 border bg-light " >
                            <Form.Group>
                                <div className="d-flex align-items-center justify-content-between mb-2">
                                    <Form.Label className="mb-0">{t("refunds")}</Form.Label>
                                    <Button variant="outline-success" size="sm" onClick={handleAddRefundItem} className="d-flex align-items-center gap-1">
                                        <AiOutlinePlus size={18} />
                                        {t("add")}
                                    </Button>
                                </div>
                                {(formData.refundsItems || []).map((item, index) => (
                                    <div key={index} className="d-flex mb-2">
                                        <Form.Control
                                            type="number"
                                            placeholder={t("value")}
                                            value={item.amount}
                                            onChange={(e) => handleRefundItemChange(index, "amount", e.target.value)}
                                            className="me-2"
                                        />
                                        <Form.Control
                                            type="text"
                                            placeholder={t("description")}
                                            value={item.description}
                                            onChange={(e) => handleRefundItemChange(index, "description", e.target.value)}
                                            className="me-2"
                                            list={`refund-descriptions-${index}`}
                                        />
                                        <datalist id={`refund-descriptions-${index}`}>
                                            {refundDescriptions.map((desc, descIndex) => (
                                                <option key={descIndex} value={desc} />
                                            ))}
                                        </datalist>
                                        <Button variant="danger" size="sm" onClick={() => handleRemoveRefundItem(index)}>🗑️</Button>
                                    </div>
                                ))}
                            </Form.Group>
                            <Form.Group className="mt-3">
                                <Form.Label>{t("total")} {t("refunds").toLocaleLowerCase()}</Form.Label>
                                <Form.Control
                                    type="number"
                                    value={(formData.refundsItems || []).reduce((s, r) => s + (parseFloat(r.amount) || 0), 0)}
                                    readOnly
                                />
                            </Form.Group>
                        </Card>
                    </Col>
                </Row>
                <Button type="submit" className="w-100 d-flex align-items-center justify-content-center gap-2 py-2" variant="primary">
                    <AiOutlineSave size={20} />
                    {id ? t("save") : t("add")}
                </Button>

            </Form>
        </Container>
    );
};

export default ExtraFundsEdit;
